/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssSelectComponent, SelectOption } from '../select/select.component';
import {
  DeliveryStatusEnum,
  DELIVERY_STATUS_ENUM_RECORD
} from '@umss/shared/data-access';

@Component({
  selector: 'umss-delivery-status-select',
  standalone: true,
  imports: [CommonModule, UmssSelectComponent],
  template: `
    <umss-select
      [options]="options"
      [placeholder]="placeholder"
      [label]="showLabel ? label : undefined"
      [helperText]="helperText"
      [errorMessage]="errorMessage"
      [size]="size"
      [disabled]="disabled"
      [required]="required"
      [error]="error"
      [loading]="{ selector: loading }"
      [multiple]="multiple"
      [hideSelectedOptions]="hideSelectedOptions"
      [(value)]="value"
      (selectionChange)="onSelectionChange($event)"
      (onFocus)="onFocus()"
      (onBlur)="onBlur()"
    />
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssDeliveryStatusSelectComponent),
      multi: true
    }
  ]
})
export class UmssDeliveryStatusSelectComponent
  implements ControlValueAccessor, OnInit
{
  @Input() showLabel = false;
  @Input() label = 'Delivery Status';
  @Input() placeholder = 'Select a delivery status';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;
  @Input() loading = false;
  @Input() multiple = false;
  @Input() hideSelectedOptions = false;

  @Input() set value(val: DeliveryStatusEnum | DeliveryStatusEnum[] | null) {
    this._value = val;
  }
  get value(): DeliveryStatusEnum | DeliveryStatusEnum[] | null {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<
    DeliveryStatusEnum | DeliveryStatusEnum[] | null
  >();
  @Output() selectionChange = new EventEmitter<
    DeliveryStatusEnum | DeliveryStatusEnum[] | null
  >();

  options: SelectOption<DeliveryStatusEnum>[] = [];
  private _value: DeliveryStatusEnum | DeliveryStatusEnum[] | null = null;

  private _onChange = (
    _value: DeliveryStatusEnum | DeliveryStatusEnum[] | null
  ): void => {
    // This is intentional - will be replaced by Angular forms
  };
  private _onTouched = (): void => {
    // This is intentional - will be replaced by Angular forms
  };

  ngOnInit(): void {
    this.initializeOptions();
  }

  private initializeOptions(): void {
    this.options = Object.entries(DELIVERY_STATUS_ENUM_RECORD).map(
      ([enumValue, label]) => ({
        value: enumValue as unknown as DeliveryStatusEnum,
        label: label
      })
    );
  }

  /** ControlValueAccessor implementation */
  writeValue(value: DeliveryStatusEnum | DeliveryStatusEnum[] | null): void {
    this._value = value;
  }

  registerOnChange(
    fn: (value: DeliveryStatusEnum | DeliveryStatusEnum[] | null) => void
  ): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onSelectionChange(
    value: DeliveryStatusEnum | DeliveryStatusEnum[] | null
  ): void {
    this._value = value;
    this._onChange(value);
    this._onTouched();
    this.valueChange.emit(value);
    this.selectionChange.emit(value);
  }

  onFocus(): void {
    // Can be used for additional logic if needed
  }

  onBlur(): void {
    this._onTouched();
  }
}
